import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { CheckCircle2, Circle, AlertTriangle, ArrowRight } from "lucide-react";

const LINK_MAP = {
  estimate: "estimate",
  subcontractors: "subcontractors",
  materials: "materials",
  stages: "",
  bills: "bills",
  hours: "hours",
  report: "report",
  chargeup: "chargeup",
};

const STATUS_META = {
  done: { Icon: CheckCircle2, cls: "text-green-400", label: "Done" },
  needs_attention: { Icon: AlertTriangle, cls: "text-amber-300", label: "Needs attention" },
  not_started: { Icon: Circle, cls: "text-neutral-500", label: "Not started" },
};

export default function JobChecklist({ projectId }) {
  const params = useParams();
  const pid = projectId || params.pid;
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get(`/projects/${pid}/checklist`);
        if (!cancel) setSteps(data.steps || []);
      } finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [pid]);

  const done = steps.filter(s => s.status === "done").length;
  const total = steps.length || 9;

  if (loading) return <div className="opacity-60 text-sm">Loading project checklist…</div>;

  return (
    <div className="card" data-testid="job-checklist">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <h3 className="text-silver">Project checklist</h3>
        <div className="text-sm text-silver" data-testid="checklist-progress">
          {done} of {total} complete
        </div>
      </div>
      <div className="h-1.5 bg-neutral-900 rounded overflow-hidden mb-3">
        <div className="h-full bg-silver transition-all" style={{ width: `${(done/total)*100}%` }}/>
      </div>
      <ul className="divide-y divide-neutral-900">
        {steps.map((s, i) => {
          const meta = STATUS_META[s.status] || STATUS_META.not_started;
          const to = `/projects/${pid}${LINK_MAP[s.link] ? "/" + LINK_MAP[s.link] : ""}`;
          return (
            <li key={s.key} className="flex items-start gap-3 py-2" data-testid={`checklist-step-${s.key}`}>
              <meta.Icon size={18} className={`mt-0.5 ${meta.cls}`}/>
              <div className="flex-1">
                <div className="text-sm">
                  <span className="opacity-50 mr-1">{i+1}.</span>
                  <span className={s.status === "done" ? "line-through opacity-70" : ""}>{s.label}</span>
                </div>
                {s.hint && <div className="text-xs text-amber-200/80 mt-0.5" data-testid={`checklist-hint-${s.key}`}>{s.hint}</div>}
              </div>
              <Link to={to} data-testid={`checklist-open-${s.key}`}
                    className="flex items-center gap-1 text-xs hub-link whitespace-nowrap">
                Open <ArrowRight size={12}/>
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
